import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Creators as SpecialistCreators } from 'store/specialists/specialists-duck.js';
import { resolveImageUrl } from 'utils/apiOrigin.js';
import { normalizePhone } from 'utils/specialists.js';

const ACCEPT = 'image/jpeg,image/png,image/webp';

const SpecialistModal = ({
  isOpen,
  specialistToUpdate,
  specialistsCloseModal,
  specialistsCreateAsync,
  specialistsUpdateAsync,
}) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      phone: '',
      active: true,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        ...specialistToUpdate,
        phone: specialistToUpdate?.phone || '',
        active: specialistToUpdate?.active ?? true,
      });
      setValue('image', null);
      return;
    }

    reset({
      name: '',
      phone: '',
      active: true,
    });
  }, [isOpen, reset, setValue, specialistToUpdate]);

  const imageField = watch('image');
  const pickedFile = imageField?.length ? imageField[0] : null;

  const previewSrc = useMemo(() => {
    if (pickedFile) {
      return URL.createObjectURL(pickedFile);
    }

    if (specialistToUpdate?.imageLink) {
      return resolveImageUrl(specialistToUpdate.imageLink);
    }

    return null;
  }, [pickedFile, specialistToUpdate]);

  const onSubmit = (data) => {
    const payload = {
      name: data.name.trim(),
      phone: normalizePhone(data.phone),
      active: data.active,
      image: pickedFile || null,
    };

    if (specialistToUpdate?.id) {
      specialistsUpdateAsync({
        id: specialistToUpdate.id,
        ...payload,
      });
      return;
    }

    specialistsCreateAsync(payload);
  };

  return (
    <Dialog open={isOpen} onClose={specialistsCloseModal} fullWidth maxWidth="sm">
      <DialogTitle>
        {specialistToUpdate?.id ? 'Editar Especialista' : 'Novo Especialista'}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Nome"
          variant="filled"
          fullWidth
          margin="normal"
          error={!!errors.name}
          helperText={errors.name?.message}
          {...register('name', { required: 'O nome do especialista e obrigatorio' })}
        />

        <TextField
          label="Telefone / WhatsApp"
          variant="filled"
          fullWidth
          margin="normal"
          error={!!errors.phone}
          helperText={
            errors.phone?.message || 'Informe preferencialmente com DDI. Ex.: 5549999999999'
          }
          {...register('phone', {
            required: 'O telefone do especialista e obrigatorio',
            validate: (value) =>
              normalizePhone(value).length >= 10 || 'Informe um telefone valido para o WhatsApp',
          })}
        />

        {previewSrc && (
          <div style={{ marginTop: 8, marginBottom: 12 }}>
            <img
              src={previewSrc}
              alt="Pre-visualizacao do especialista"
              style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 8 }}
            />
          </div>
        )}

        <input
          type="file"
          accept={ACCEPT}
          {...register('image')}
          style={{ marginTop: 8, marginBottom: 8, width: '100%' }}
        />

        <Controller
          name="active"
          control={control}
          defaultValue={specialistToUpdate?.active ?? true}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch checked={!!value} onChange={(event) => onChange(event.target.checked)} />
              }
              label={value ? 'Ativo' : 'Inativo'}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={specialistsCloseModal} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.Specialist.isModalOpen,
  specialistToUpdate: state.Specialist.specialistToUpdate,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(SpecialistCreators, dispatch);

SpecialistModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  specialistToUpdate: PropTypes.object,
  specialistsCloseModal: PropTypes.func.isRequired,
  specialistsCreateAsync: PropTypes.func.isRequired,
  specialistsUpdateAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(SpecialistModal);
